import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';
import { SEO } from "../components/SEO.tsx";

interface FAQ {
  id: string;
  title: string;
  content: string;
  order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function FAQs() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [expandedFaqs, setExpandedFaqs] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_faqs');

      if (error) throw error;
      
      // If no FAQs exist yet, create initial FAQs
      if (!data || data.length === 0) {
        await createInitialFaqs();
        const { data: newData, error: newError } = await supabase.rpc('get_faqs');
        if (newError) throw newError;
        setFaqs(newData || []);
      } else {
        setFaqs(data);
      }
    } catch (err) {
      logger.error('Error fetching FAQs:', err);
    } finally {
      setLoading(false);
    }
  };

  const createInitialFaqs = async () => {
    const initialFaqs = [
      {
        title: "What is Wayja?",
        content: "Wayja is a prediction markets platform where users can create and participate in markets predicting various outcomes. Users can place trades on their predictions and earn rewards for correct predictions."
      },
      {
        title: "How do I get started?",
        content: "To get started, create an account and verify your email. You'll need to deposit funds to start participating in markets. Browse existing markets or create your own (subject to approval)."
      },
      {
        title: "How do prediction markets work?",
        content: "Prediction markets allow users to trade on the outcome of future events. Each market has multiple possible outcomes, and users can place trades on these outcomes. When the market resolves, winning trades are paid out based on the odds, while losing trades are forfeited."
      },
      {
        title: "How do I create a market?",
        content: "To create a market, click the 'Create Market' button and fill out the required information, including the market question, possible outcomes, and resolution criteria. Your market will be reviewed by administrators before being published."
      },
      {
        title: "What are the platform fees?",
        content: "Wayja charges a service fee of 8% on all trade placements. This fee is automatically deducted when you place a trade. For example, if you place a R100 trade, the service fee would be R8."
      },
      {
        title: "How are markets resolved?",
        content: "Markets are resolved by administrators based on objective criteria specified when the market was created. Resolution decisions are final and binding, though users may appeal within 24 hours of resolution."
      },
      {
        title: "How do I withdraw my winnings?",
        content: "You can withdraw your winnings at any time through the 'Withdraw' section of your account. Withdrawals are processed within 24-48 hours, depending on your chosen withdrawal method."
      },
      {
        title: "What happens if I violate platform rules?",
        content: "Violations of platform rules may result in warnings, temporary suspension, or permanent account termination, depending on the severity of the violation. We take platform integrity seriously to ensure fair and enjoyable participation for all users."
      },
      {
        title: "How do I contact support?",
        content: "You can contact our support team through the 'Contact Support' page. We aim to respond to all inquiries within 24 hours. For urgent matters, please include 'URGENT' in your message subject."
      },
      {
        title: "Are there any other fees?",
        content: "The only fee charged by Wayja is the 8% service fee on trade placements. There are no additional fees for creating markets, withdrawing funds, or using any other platform features."
      }
    ];

    try {
      for (const faq of initialFaqs) {
        await supabase.rpc('add_faq', {
          p_title: faq.title,
          p_content: faq.content
        });
      }
    } catch (err) {
      logger.error('Error creating initial FAQs:', err);
    }
  };

  const toggleFaq = (id: string) => {
    setExpandedFaqs(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filteredFaqs = faqs.filter(faq => 
    faq.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <SEO
        title="FAQs"
        description="Find answers to frequently asked questions about Wayja prediction markets. Learn how to place trades, create markets, withdraw winnings, and more."
        url="/faqs"
      />
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h1>
      
      <div className="relative mb-8">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search FAQs..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-wayja-teal"
        />
      </div>

      <div className="space-y-4">
        {filteredFaqs.length === 0 ? (
          <p className="text-gray-600 text-center py-8">
            No FAQs found matching your search. Please try different keywords or contact support for assistance.
          </p>
        ) : (
          filteredFaqs.map(faq => (
            <div
              key={faq.id}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleFaq(faq.id)}
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-900">{faq.title}</span>
                {expandedFaqs.has(faq.id) ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {expandedFaqs.has(faq.id) && (
                <div className="px-6 py-4 bg-gray-50">
                  <p className="text-gray-600 whitespace-pre-wrap">{faq.content}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="mt-12 text-center">
        <p className="text-gray-600">
          Still have questions? Feel free to{' '}
          <a href="/contact-support" className="text-wayja-teal hover:underline">
            contact our support team
          </a>
          .
        </p>
      </div>
    </div>
  );
} 